<script setup lang="ts">
import { computed, ref } from 'vue';
import { searchCompanies } from '@/services/companySearch';
import type { CompanyInfo } from '@/services/types';

/**
 * Company search against Enhetsregisteret.
 *
 * Converted from demosearch.vue. The data source moved from Eniro to BRREG, so
 * the attribution changed and the result rows now carry organisation form and
 * industry, which Eniro did not provide. Only companies BRREG's address could be
 * geocoded are clickable, since the rest have nowhere to put a map pin.
 */

const emit = defineEmits<{ 'company-click': [company: CompanyInfo] }>();

const MAX_RESULTS = 20;

const searchText = ref('');
const errorMessage = ref('');
const searching = ref(false);
const hasSearched = ref(false);
const totalHits = ref(0);
const companies = ref<CompanyInfo[]>([]);

const resultMessage = computed(() => {
  if (!hasSearched.value) {
    return 'Skriv inn søketekst og klikk søk eller trykk enter..';
  }

  if (companies.value.length === 0) {
    return 'Ingen firma funnet.';
  }

  const note = 'Firma med kjent posisjon kan klikkes for visning i kart.';

  return totalHits.value > companies.value.length
    ? `${totalHits.value} treff — ${note} Maks ${MAX_RESULTS} i visning.`
    : `${totalHits.value} treff — ${note}`;
});

async function search(): Promise<void> {
  errorMessage.value = '';

  if (!searchText.value.trim()) {
    companies.value = [];
    totalHits.value = 0;
    hasSearched.value = false;
    return;
  }

  searching.value = true;

  try {
    const result = await searchCompanies(searchText.value.trim());
    companies.value = result.companies;
    totalHits.value = result.totalHits;
    hasSearched.value = true;
  } catch (error) {
    errorMessage.value = 'Beklager, det oppsto en feil. Prøv gjerne igjen.';
    console.error('Firmasøk feilet', error);
  } finally {
    searching.value = false;
  }
}

function selectCompany(company: CompanyInfo): void {
  if (company.location) {
    emit('company-click', company);
  }
}

function describe(company: CompanyInfo): string {
  return [company.address, company.postalCode, company.postalArea].filter(Boolean).join(' ');
}
</script>

<template>
  <div>
    <div class="ui basic segment search-inputs">
      <h3 class="ui header">Firmasøk</h3>
      <div class="ui action fluid input">
        <input
          v-model="searchText"
          type="text"
          placeholder="Søketekst.."
          aria-label="Firmanavn"
          @keyup.enter="search"
        />
        <button class="ui darkpurple button" :class="{ loading: searching }" @click="search">
          Søk
        </button>
      </div>
      <div class="search-attribution">
        Data fra
        <a href="https://data.brreg.no/enhetsregisteret/" target="_blank" rel="noopener">
          Enhetsregisteret
        </a>
        — posisjon fra
        <a href="https://kartverket.no/" target="_blank" rel="noopener">Kartverket</a>
      </div>
      <div v-if="errorMessage" class="search-errors" role="alert">{{ errorMessage }}</div>
    </div>

    <div class="ui divider" />

    <div class="company-list">
      <div class="ui basic segment">
        <div class="ui top attached label">{{ resultMessage }}</div>
        <div class="ui relaxed selection list">
          <div
            v-for="company in companies"
            :key="company.id"
            class="item"
            :class="{ disabled: !company.location }"
            @click="selectCompany(company)"
          >
            <i class="building outline icon" />
            <div class="content">
              <span class="header">{{ company.name }}</span>
              <div class="description">
                {{ describe(company) }}
                <span v-if="company.organisationForm" class="company-meta">
                  · {{ company.organisationForm }}
                </span>
                <span v-if="company.isBankrupt" class="company-bankrupt">· konkurs</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.search-inputs {
  max-width: 330px;
  margin-bottom: 0;
  padding-bottom: 0;
}

.search-errors {
  margin-top: 5px;
  color: #a03328;
}

.search-attribution {
  font-size: 0.7rem;
  margin-top: 4px;
}

.company-meta {
  color: #888;
}

.company-bankrupt {
  color: #a03328;
  font-weight: 600;
}
</style>
